import React from 'react';
import AddItemIcon from '@mui/icons-material/AddCircle';
import CalculateIcon from '@mui/icons-material/Calculate';
import EditIcon from '@mui/icons-material/Edit';
import { Divider } from '@mui/material';

import { hasLayoutOptimizationFeatureFlag } from '@paragon/login-tools';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { useAppMonitor } from '../../../../monitor';
import { startInsertingFeatureArea } from '../../../../store/actions/layout/featureAreaActions';
import { optimizeFeaturePatternContainerLayout } from '../../../../store/actions/layout/layoutActions';
import { startInsertTrussEnvelope } from '../../../../store/actions/layout/trussEnvelopeActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemsEditPropertiesAction,
} from '../../../../store/actions/sceneInteractionActions';
import { isInsertingAnyObject } from '../../../../store/selectors/insertionObjectSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function FeaturePatternContainerMenu(props: { guid: string }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const isInsertingObject = useAppSelector(isInsertingAnyObject);

  const layoutOptimizationEnabled = useAppSelector(hasLayoutOptimizationFeatureFlag);

  const container = useAppSelector((state) => state.layout.featurePatternContainers[props.guid]);
  if (container == null) throw new Error('Unable to find feature pattern container with active menu.');

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={async () => {
          await dispatch(handleStandardSceneItemEditPropertiesAction('featurePatternContainer', container));
          monitor.recordInteraction('Feature Area => Edit Properties menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Add Feature"
        disabled={isInsertingObject}
        icon={<AddItemIcon />}
        onClick={async () => {
          dispatch(startInsertingFeatureArea(props.guid));
          monitor.recordInteraction('Feature Pattern Container => Add Feature menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Add Truss Envelope"
        disabled={isInsertingObject}
        icon={<AddItemIcon />}
        onClick={async () => {
          dispatch(startInsertTrussEnvelope({ featurePatternContainerGuid: props.guid }));
          monitor.recordInteraction('Feature Pattern Container => Add Truss Envelope menu item clicked');
        }}
      />

      {layoutOptimizationEnabled && (
        <>
          <Divider />

          <SceneMenuItem
            text="Place Optimized Truss Envelopes"
            icon={<CalculateIcon />}
            onClick={async () => {
              await dispatch(optimizeFeaturePatternContainerLayout(props.guid));
              monitor.recordInteraction('Place Optimized Truss Envelopes button clicked');
            }}
          />
        </>
      )}

      <Divider />

      <DeleteMenuItem type="featurePatternContainer" userVisibleTerm="Container" item={container} />
    </SceneMenuContent>
  );
}

export function BulkFeaturePatternContainerMenu(props: { items: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const featurePatternContainerStore = useAppSelector((state) => state.layout.featurePatternContainers);
  const featurePatternContainers = props.items.map((item) => featurePatternContainerStore[item.guid]);

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={() => {
          dispatch(handleStandardSceneItemsEditPropertiesAction('featurePatternContainer', featurePatternContainers));
          monitor.recordInteraction('Feature Pattern Containers => Edit Properties menu item clicked');
        }}
      />

      <Divider />

      <DeleteMenuItem type="featurePatternContainer" userVisibleTerm="Containers" item={props.items} />
    </SceneMenuContent>
  );
}
